"use client"

import type { Offer } from "@local/offer/src/types"
import { getCdnUrl } from "@local/utils/src/getCdnUrl"
import Image from "next/image"
import { useState } from "react"
import { Button } from "../../uiv7/Button"
import { ContentBlock } from "../../ui/ContentBlock"
import { cn } from "@local/utils/src/cn"

export interface UserGeneratedContentProps {
  content: Offer.Ugc
}

export const UserGeneratedContent = ({
  content,
}: UserGeneratedContentProps) => {
  const [showAll, setShowAll] = useState(false)

  const columns = content.columns || 4
  const totalImages = content.images?.length || 0
  const totalRows = Math.ceil(totalImages / columns)
  const shouldShowSeeMore = totalRows > 1

  const itemsToShow = showAll
    ? content.images
    : content.images?.slice(0, columns)

  return (
    <ContentBlock touchEdges className="mb-4 py-10 text-gray-900">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <h3
          className="text-h3 leading-heading-tight mb-6 text-center font-semibold"
          dangerouslySetInnerHTML={{ __html: content.headline || "" }}
        />
      </div>
      <div className="relative w-full">
        <div
          className={cn(
            "grid",
            columns === 4
              ? "grid-cols-2 md:grid-cols-4"
              : "grid-cols-2 sm:grid-cols-3"
          )}
        >
          {itemsToShow?.map(
            (image) =>
              image?.url && (
                <div
                  key={`ugc-item-${image.id}`}
                  className="relative aspect-square overflow-hidden"
                >
                  <Image
                    src={getCdnUrl({
                      src: image.url as string,
                      width: image.width || 400,
                    })}
                    alt={
                      (image.alt as string) ||
                      "BÆRSkin customer wearing tactical gear"
                    }
                    fill
                    className="object-cover transition-transform duration-300 hover:scale-105"
                    sizes={
                      columns === 4
                        ? "(max-width: 640px) 50vw, (max-width: 768px) 33vw, 25vw"
                        : "(max-width: 640px) 50vw, 33vw"
                    }
                  />
                </div>
              )
          )}
        </div>
        {/* Shadow overlay */}
        {shouldShowSeeMore && !showAll && (
          <div className="pointer-events-none absolute right-0 bottom-0 left-0 h-20 bg-gradient-to-b from-transparent via-white to-white" />
        )}
      </div>
      {shouldShowSeeMore && !showAll && (
        <div className="mt-6 flex justify-center px-4">
          <Button type="button" onClick={() => setShowAll(true)}>
            See more
          </Button>
        </div>
      )}
    </ContentBlock>
  )
}
